/**
 * The page's one read: `GET /functions/v1/menu_context`.
 *
 * The publishable key goes on the `apikey` header and only there
 * (`supabaseFetch` is what enforces that, and throws if a secret key is ever
 * handed to it). What comes back is `public.menu_context()`: the allow-listed
 * register of §12, every occasion of §6.1, the §5.3 page groups, the delivery
 * zones of §8.1, the calendar and lane window for the date, the slots, the
 * publication, and §11.3's and §11.4's capacity rules as answers rather than
 * as figures.
 *
 * §19.2 principle 2 is unchanged by any of it: what the page works out from
 * this reading is display only, and `submit_order` re-runs every rule against
 * `public.submission_context()`, which is a wider read no browser can make.
 */

import type { OccasionRow, Params, PublishedDish, ZoneRow } from '@plt/engine'

import { supabaseFetch } from './api.ts'
import type { PublicConfig } from './env.ts'

export interface MenuGroupRow {
  group_key: string
  side: string
  name_hu: string | null
  name_en: string | null
  sort_order: number
}

export interface MenuOccasionRow extends OccasionRow {
  name_hu: string | null
  name_en: string | null
  reference_window: string | null
}

export interface SlotRow {
  slot_id: string
  slot_date: string
  starts_at: string
  ends_at: string
  offered: boolean
  reason: string | null
}

export interface PostcodeDelivery {
  postcode: string | null
  known: boolean
  settlements: string[]
  district: string | null
  distance_km: number | null
  zone: string
  deliverable: boolean
  budapest: boolean
  /** `delivery_max_km`, for the sentence beyond the radius. */
  max_km: number | null
}

export interface MenuContext {
  ok: boolean
  reason?: string
  now: string
  today: string
  event_date: string | null
  /** The date the page opens on: ten business days ahead, as vB6 opened. */
  suggested_event_date: string | null
  params: Params
  ruleset_version: number | null
  catalogue_version: string | null
  /**
   * True while `CATALOGUE_ALLOW_UNVERIFIED` is on for this database: dishes
   * whose allergens Robi has not verified are published, and every page and
   * customer email carries the test-system banner. Absent on an older
   * function, which reads as false. Never true in production: the database
   * refuses to hold it there.
   */
  catalogue_unverified?: boolean
  occasions: MenuOccasionRow[]
  menu_groups: MenuGroupRow[]
  zones: Record<string, ZoneRow>
  calendar: {
    is_business_day: boolean
    kitchen_closed: boolean
    holiday_name: string | null
    closed: boolean
    is_clash_date: boolean
  } | null
  window: {
    lane_window: 'standard' | 'short_notice' | 'too_late'
    notice_deadline: string
    short_notice_deadline: string
    business_days_left: number
  } | null
  deadlines: Record<
    string,
    { notice_deadline: string; freeze_at: string | null; payment_deadline: string | null }
  > | null
  fulfilment: string
  postcode: string | null
  zone: string
  /**
   * Specification §3.7 (DECIDED 20261023): `public.postcode_delivery()` for
   * the postcode asked about — its settlements, the distance from Graphisoft
   * Park, the band (the same as `zone`) and whether delivery is offered
   * there. Absent from a context read before `20261023100000`, which reads
   * as deliverable.
   */
  delivery?: PostcodeDelivery
  slots: SlotRow[]
  /** §11.3 and §11.4 as answers. No cap and no committed figure is sent;
   *  since run 9 item 4 the room left before portion_cap and
   *  max_covers_per_day is, as `soft_portions_left` already was, so the page
   *  can send an order for confirmation before it is sent. */
  capacity: {
    /** Since run 9 item 4: the day's order count is reached. Nothing else refuses. */
    day_full: boolean
    soft_portions_left: number | null
    /** Run 9, item 4: room before portion_cap and max_covers_per_day. Absent
     *  from a context captured before `20261010191000`, which reads as unknown. */
    portions_left?: number | null
    covers_left?: number | null
  }
  /** §8.3: how many more pieces the short-notice lane takes for this date. */
  short_notice: { pieces_left: number | null }
  /**
   * `build/FIXES.md` item 79 (F8): the instant Planteen has promised an
   * answer by for a request sent now — §11.4's `review_in` / `review_out`
   * walked over §8.1's business-day calendar.
   *
   * It comes from the server because the walk needs the calendar, and the
   * page has the two times but not the business days. `submission_context`
   * carries the same key, from the same function, so the page's late-order
   * block and the server's refusal are the same judgement.
   */
  confirmation_answer_due?: string | null
  dishes: PublishedDish[]
  calendar_from?: string
  calendar_to?: string
}

export interface MenuContextQuery {
  date?: string | null
  start?: string | null
  postcode?: string | null
  fulfilment?: 'delivery' | 'pickup'
}

/**
 * What came back when the read did not succeed.
 *
 * `serverMessage` is the one plain sentence `menu_context` sends, and it is
 * **Hungarian** — release one sends Hungarian only (§5.12, and
 * `supabase/functions/_shared/messages.ts`). It is `null` when the function
 * answered with no sentence at all, which is the case the page has to word
 * for itself; this module invents no customer copy, so that the page can say
 * it in whichever language the customer is reading.
 */
export class MenuContextError extends Error {
  readonly status: number
  readonly serverMessage: string | null
  constructor(status: number, serverMessage: string | null) {
    super(serverMessage ?? `menu_context answered ${status}`)
    this.name = 'MenuContextError'
    this.status = status
    this.serverMessage = serverMessage
  }
}

export function menuContextPath(query: MenuContextQuery): string {
  const search = new URLSearchParams()
  if (query.date) search.set('date', query.date)
  if (query.start) search.set('start', query.start)
  if (query.postcode) search.set('postcode', query.postcode)
  if (query.fulfilment) search.set('fulfilment', query.fulfilment)
  const tail = search.toString()
  return `/functions/v1/menu_context${tail === '' ? '' : `?${tail}`}`
}

export async function readMenuContext(
  config: PublicConfig,
  query: MenuContextQuery,
  fetchImpl: typeof fetch = fetch,
): Promise<MenuContext> {
  const response = await supabaseFetch(
    config.supabaseUrl,
    menuContextPath(query),
    { method: 'GET', apiKey: config.supabasePublishableKey },
    fetchImpl,
  )
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string }
    throw new MenuContextError(response.status, body.message ?? null)
  }
  const body = (await response.json()) as { context: MenuContext }
  return body.context
}
